import {
    Material,
    Mesh,
    MeshAttribute,
    MeshComponent,
    Object3D,
} from '@wonderlandengine/api';
import {AnimationController, Easing, Interpolation, Tween} from '@wonderlandengine/tweenjs';
import {Ref, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {FrameUpdateContext, MainUI} from '../main-ui.js';
import {EasingFunction} from '@wonderlandengine/tweenjs/dist/tweenjs/Easing.js';
import {Color} from '../utils/utils.js';

const tempPosLength = 32;
const tempPositions = new Float32Array(2 * tempPosLength);

/** Get bounding box of a mesh */
function calculateMeshBoundingBox(m: Mesh, min: Float32Array, max: Float32Array) {
    if (m.vertexCount === 0) return;

    const pos = m.attribute(MeshAttribute.Position);

    pos.get(0, min);
    max.set(min);

    for (let i = 1; i < pos.length; i += tempPosLength) {
        const p = pos.get(i, tempPositions);

        const end = Math.min(tempPosLength, pos.length - i);
        for (let x = 0; x < end; ++x) {
            min[0] = Math.min(p[x * 3], min[0]);
            min[1] = Math.min(p[x * 3 + 1], min[1]);
            min[2] = Math.min(p[x * 3 + 2], min[2]);

            max[0] = Math.max(p[x * 3], max[0]);
            max[1] = Math.max(p[x * 3 + 1], max[1]);
            max[2] = Math.max(p[x * 3 + 2], max[2]);
        }
    }
}

/**
 * Hook to get the bounding box of the first mesh on the first mesh
 * component of a object ref.
 *
 * Limitations: When the mesh updates, the bounding box is not recalcuated.
 */
function getBoundingBox(
    ref: React.MutableRefObject<Object3D>,
    min: Float32Array,
    max: Float32Array
) {
    if (!ref.current) return;

    const c = ref.current.findByNameDirect('mesh')[0];
    const m = c?.getComponent(MeshComponent);
    if (m && m.mesh) {
        calculateMeshBoundingBox(m.mesh, min, max);
    }
}

const tempVecPosition = new Float32Array(3);
const tempVecScale = new Float32Array(3);

export interface AnimationProps {
    repeatDelay?: number;
    interpolation?: (v: number[], k: number) => number;
    easing?: EasingFunction;
    /**
     * Relative x and y position
     */
    position?: {from?: number[]; to: number[]};
    /**
     * Rotation in degrees, around z axis.
     */
    rotation?: {from?: number; to: number};
    /**
     * X and Y scale
     */
    scale?: {from?: number[]; to: number[]};
    repeat?: boolean | number;
    yoyo?: boolean;
    duration?: number;
    delay?: number;
}
/**
 * Applies a Tween animation to the underlying Wonderland object3D.
 * All animations are relative to the current position, rotation and scale,
 * and to the center of the bounding box.
 *
 * Note: Because of an issue with the current version of the API, it is nessesary to
 * call the function returned by the hook to set the ref of the object3D when the reference
 * changes due to an object becoming active.
 * @returns A ref to the object3D and a function to set the ref.
 */
export const useAnimation = (
    props: AnimationProps
): [React.MutableRefObject<Object3D | null>, (o: Object3D) => void] => {
    const ref = useRef<Object3D>(null);
    const [refState, setRefState] = useState<Object3D | null>(null);
    const min = useMemo(() => new Float32Array(3), []);
    const max = useMemo(() => new Float32Array(3), []);
    const updateFrame = useContext(FrameUpdateContext);

    const animationTween = useRef<Tween>(null);

    useEffect(() => {
        setRefState(ref.current);
    }, [ref.current]);

    // TK: This function is used as a bit of a hack to set the ref of the object3D when the reference changes.
    // This happens when an component is used conditionally and becomes active. If this is not called, the ref.current
    // value will stay null.
    const srf = (o: Object3D) => {
        ref.current = o;
        setRefState(o);
    };

    //Set up the main update loop for the animation.
    useEffect(() => {
        let animationInitialized = false;
        const updateAnimations = (dt) => {
            //TK: We need to first check
            if (!animationInitialized) {
                // reference not set, return
                if (!ref.current) return;
                let child = ref.current?.findByNameDirect('mesh')[0];
                // mesh object not found, animate the object itself
                if (!child) {
                    child = ref.current;
                }

                // we have a mesh object, set up the animation
                let startPosition = child.getPositionLocal();
                let scaleLocal = child.getScalingLocal();
                const startObject = {pos: {x: 0, y: 0}, scale: {x: 1, y: 1}};
                const target = {pos: {x: 0, y: 0}, scale: {x: 1, y: 1}};
                if (props.position) {
                    if (props.position.from) {
                        startObject.pos = {
                            x: props.position.from[0],
                            y: props.position.from[1],
                        };
                    }
                    target.pos = {
                        x: props.position.to[0],
                        y: props.position.to[1],
                    };
                }

                if (props.scale) {
                    if (props.scale.from) {
                        startObject.scale = {
                            x: props.scale.from[0],
                            y: props.scale.from[1],
                        };
                    }
                    target.scale = {
                        x: props.scale.to[0],
                        y: props.scale.to[1],
                    };
                }

                animationTween.current = new Tween(startObject)
                    .to(target, props.duration ?? 1000)
                    .delay(props.delay ?? 0)
                    .repeat(
                        props.repeat === true
                            ? Infinity
                            : props.repeat === false
                              ? 0
                              : props.repeat
                    )
                    .easing(props.easing ?? Easing.Linear.None)
                    .yoyo(props.yoyo ?? false)
                    .repeatDelay(props.repeatDelay ?? 0)
                    .interpolation(props.interpolation ?? Interpolation.CatmullRom)
                    .onUpdate((v) => {
                        if (!child || child.isDestroyed) {
                            return;
                        }
                        if (props.position) {
                            // no need to update if there's no position animation set
                            tempVecPosition[0] =
                                startPosition[0] + v.pos.x * updateFrame.relativeScale[0]; //scaleWorld[0]; //, start[0] - 0.5 * scaleWorld[0]]}
                            tempVecPosition[1] =
                                startPosition[1] + v.pos.y * updateFrame.relativeScale[1]; //, start[0] - 0.5 * scaleWorld[0]]}
                            tempVecPosition[2] = startPosition[2];
                            child.setPositionLocal(tempVecPosition);
                        }

                        if (props.scale) {
                            tempVecScale[0] = scaleLocal[0] * v.scale.x;
                            tempVecScale[1] = scaleLocal[1] * v.scale.y;
                            tempVecScale[2] = scaleLocal[2];
                            child.setScalingLocal(tempVecScale);
                        }
                    })
                    .start();
                animationInitialized = true;
            }
            if (!ref.current) {
                // we had an animation and it is gone now?
                if (animationTween.current !== null) {
                    animationTween.current.stop();
                    animationTween.current = null;
                }
                animationInitialized = false; // stop the update loop
            } else if (animationTween.current !== null) {
                animationTween.current.update();
            }
        };

        updateFrame.updateEvent.add(updateAnimations);

        return () => {
            updateFrame.updateEvent.remove(updateAnimations);
        };
    }, [refState]);

    return [ref, srf];
};

// export const useMaterialAnimation = (
//     props: AnimationProps & {
//         material: Material;
//         startcolor: [];
//         endColor: [];
//         duration?: number;
//     }
// ): [] => {
//     const ref = useRef<Material>(null);
//     const [refState, setRefState] = useState<Material | null>(null);
//     const updateFrame = useContext(FrameUpdateContext);

//     const animationTween = useRef<Tween>(null);

//     //Set up the main update loop for the animation.
//     useEffect(() => {
//         let animationInitialized = false;
//         const updateAnimations = (dt) => {
//             if (!animationInitialized) {
//                 if (!ref.current) return;

//                 animationTween.current = new Tween({})
//                     .to({}, props.duration ?? 1000)
//                     .delay(props.delay ?? 0)
//                     .repeat(
//                         props.repeat === true
//                             ? Infinity
//                             : props.repeat === false
//                               ? 0
//                               : props.repeat
//                     )
//                     .easing(props.easing ?? Easing.Linear.None)
//                     .yoyo(props.yoyo ?? false)
//                     .repeatDelay(props.repeatDelay ?? 0)
//                     .interpolation(props.interpolation ?? Interpolation.CatmullRom)
//                     .onUpdate((v) => {
//                         if (!ref.current || ref.current.isDestroyed) {
//                             return;
//                         }
//                     })
//                     .start();
//                 animationInitialized = true;
//             }
//             return [];
//         };
//     }, []);

//     return [];
// };
